import { toast } from "react-toastify"
import type { BaseQueryFn } from "@reduxjs/toolkit/query"
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"
import axios from "axios"
import Cookies from "js-cookie"
import storage from "redux-persist/lib/storage"

import Toast from "@/components/ui/toast"
import { apiErrorDetails, isCancelledRequest, isSessionFailure, requireSuccessfulResponse } from "@/lib/api-response"
import { isCloudflarePagesHostname } from "@/lib/previewBackend"
import routes from "@/routes/routes"
import { clearAuthCookies } from "@/utils/authUtils"

const INTERNAL_CLIENT_HEADER = "X-Internal-Client"
const INTERNAL_CLIENT_NAME = "Moogli-Frontend"

let isRefreshing = false
let refreshTokenPromise: Promise<boolean> | null = null

const clearSessionAndRedirect = (message: string) => {
  clearAuthCookies()
  // Wipe the redux-persist localStorage snapshot so stale data from the
  // previous session cannot re-hydrate when the page reloads after redirect.
  storage.removeItem("persist:root")
  Toast({ type: "error", message })
  if (!import.meta.env.DEV && window.location.pathname !== routes.login) {
    window.location.replace(routes.login)
  }
}

const refreshAccessToken = async (baseUrl: string): Promise<boolean> => {
  try {
    const response = await axios.post(
      `${baseUrl}/v1/auth/rotate-tokens`,
      {},
      {
        withCredentials: true,
        headers: {
          [INTERNAL_CLIENT_HEADER]: INTERNAL_CLIENT_NAME,
        },
      },
    )
    requireSuccessfulResponse(response)
    return true
  } catch (error) {
    // An expired/revoked refresh token is a handled sign-in transition.
    const status = (error as AxiosError)?.response?.status
    if (status !== 401 && status !== 403) console.error("Error refreshing access token", error)
    return false
  }
}

export type AxiosBaseQueryArgs = {
  url: string
  method: AxiosRequestConfig["method"]
  data?: AxiosRequestConfig["data"]
  params?: AxiosRequestConfig["params"]
  timeoutMs?: number
  suppressErrorToast?: boolean
  parseResponse?: (response: AxiosResponse) => unknown
  rejectFailureResponse?: boolean
  /** Upload progress of the request body. Only passed to axios when given. */
  onUploadProgress?: AxiosRequestConfig["onUploadProgress"]
  /** Answers the request instead of the network (Content Studio mock mode). Only passed when given. */
  adapter?: AxiosRequestConfig["adapter"]
}

export const axiosBaseQuery =
  ({ baseUrl }: { baseUrl: string } = { baseUrl: "" }): BaseQueryFn<AxiosBaseQueryArgs> =>
  async (args, api, _extraOptions) => {
    const {
      url,
      method,
      data,
      params,
      suppressErrorToast,
      parseResponse,
      rejectFailureResponse,
      onUploadProgress,
      adapter,
    } = args
    const config: AxiosRequestConfig = {
      url: baseUrl + url,
      method,
      data,
      params,
      signal: api.signal,
      timeout: args.timeoutMs,
      withCredentials: true,
      headers: { [INTERNAL_CLIENT_HEADER]: INTERNAL_CLIENT_NAME },
      ...(onUploadProgress ? { onUploadProgress } : {}),
      ...(adapter ? { adapter } : {}),
    }
    const send = async () => {
      const response = await axios(config)
      if (rejectFailureResponse) requireSuccessfulResponse(response)
      return { data: parseResponse ? parseResponse(response) : response.data }
    }
    const failed = (error: AxiosError) => {
      const details = apiErrorDetails(error)
      return {
        error: {
          status: error.response?.status,
          data: error.response?.data ?? error.message,
          ...(details.code ? { code: details.code } : {}),
          ...(details.requestId ? { requestId: details.requestId } : {}),
        },
      }
    }
    try {
      return await send()
    } catch (axiosError) {
      const err = axiosError as AxiosError
      // Axios supplies config; use our own for adapters/mocks that omit it.
      const isAuthenticationError = isSessionFailure({ ...err, config }, true)

      if (isAuthenticationError) {
        const hasClientReadableSession = Boolean(Cookies.get("refreshToken"))

        if (!isRefreshing) {
          isRefreshing = true
          refreshTokenPromise = refreshAccessToken(baseUrl).finally(() => {
            isRefreshing = false
            refreshTokenPromise = null
          })
        }

        const didRefresh = await refreshTokenPromise

        // A failed rotation means the session is no longer recoverable. Do not
        // retry the original request with the same invalid credentials.
        if (!didRefresh) {
          // Direct preview-to-API requests keep cookies on the API host, so the
          // app cannot read them. Let the route-level session check redirect
          // silently in that case; only clear a session the client could see.
          if (hasClientReadableSession) {
            clearSessionAndRedirect("Session expired. Please log in again.")
          }

          return failed(err)
        }

        // Retry the original request after token refresh
        try {
          return await send()
        } catch (retryError) {
          const retryErr = retryError as AxiosError
          if (!suppressErrorToast && !isCancelledRequest(retryErr))
            Toast({ type: "error", message: apiErrorDetails(retryErr).message })
          return failed(retryErr)
        }
      }

      if (!suppressErrorToast && !isCancelledRequest(err)) {
        if (err.code === "ERR_NETWORK") {
          toast.clearWaitingQueue()
          Toast({ type: "error", message: err.message, options: { toastId: err.code } })
        } else {
          const errorMessage = apiErrorDetails(err).message
          Toast({ type: "error", message: errorMessage, options: { toastId: errorMessage } })
        }
      }

      return failed(err)
    }
  }

export function getAxiosBaseUrl(hostname = typeof window === "undefined" ? "" : window.location.hostname) {
  // Cloudflare previews must keep authentication first-party. Calling the
  // backend host directly turns the session cookies into third-party cookies,
  // which privacy-focused browsers can reject even with SameSite=None.
  if (isCloudflarePagesHostname(hostname)) {
    return "/api"
  }

  return import.meta.env.VITE_API_BASE_URL || "/api"
}
